import { useCallback, useEffect } from "react"
import { useProfileTabAutoSwitch } from "@/hooks/use-profile-tab-auto-switch"
import { DefaultInspirationPerPage } from "@/types/inspiration"
import { useInView } from "react-intersection-observer"

import { DEFAULT_PLACES_PER_PAGE } from "@/lib/paging"
import { useGetUserReviewsQuery } from "@/lib/storage/api/place-api"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import publicProfileSlice, {
  PublicProfileTabType,
  selectPublicCollections,
  selectPublicCollectionsCount,
  selectPublicCurrCollectionsPage,
  selectPublicCurrInspirationPage,
  selectPublicGuides,
  selectPublicGuidesCount,
  selectPublicLoadingCollections,
  selectPublicLoadingGuides,
  selectPublicMaxCollectionsPage,
  selectPublicMaxInspirationPage,
  selectPublicTab,
  selectPublicUser,
  selectPublicUserProfile,
} from "@/lib/storage/public-profile-slice"
import {
  handlePublicUserCollectionsNext,
  handlePublicUserGuidesNext,
  setPublicTabWithData,
} from "@/lib/storage/public-profile-thunks"
import { selectFollowStatus } from "@/lib/storage/user-slice"
import {
  isFollowingStatus,
  isSearchable,
  userDisplayShortName,
} from "@/lib/user-utils"
import { Container } from "@/components/container"
import IconChecklist from "@/components/icons/checklist"
import IconHeart from "@/components/icons/heart"
import IconLock from "@/components/icons/lock"
import IconStar from "@/components/icons/star"
import { LottieAnimation } from "@/components/ui/lottie-animation"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { CardGrid } from "./card-grid"
import { CollectionCard } from "./collection-card"
import { InspirationCard } from "./inspiration-card"
import { LoadingPageIndicator } from "./loading-page-indicator"
import { ProfileHeader } from "./profile-header"
import { ProfileReviewsList } from "./profile-reviews-list"

export function PublicProfile({ handle }: { handle: string }) {
  const dispatch = useAppDispatch()
  const guides = useAppSelector(selectPublicGuides)
  const collections = useAppSelector(selectPublicCollections)
  const currInspirationPage = useAppSelector(selectPublicCurrInspirationPage)
  const maxInspirationPage = useAppSelector(selectPublicMaxInspirationPage)
  const currCollectionsPage = useAppSelector(selectPublicCurrCollectionsPage)
  const maxCollectionsPage = useAppSelector(selectPublicMaxCollectionsPage)
  const loadingGuides = useAppSelector(selectPublicLoadingGuides)
  const loadingCollections = useAppSelector(selectPublicLoadingCollections)
  const tab = useAppSelector(selectPublicTab)
  const user = useAppSelector(selectPublicUser)
  const userProfile = useAppSelector(selectPublicUserProfile)
  const guidesCount = useAppSelector(selectPublicGuidesCount)
  const collectionsCount = useAppSelector(selectPublicCollectionsCount)
  const followStatus = useAppSelector(
    (state) => user?.user_ref && selectFollowStatus(state, user.user_ref)
  )
  const acceptedFollower = isFollowingStatus(followStatus)
  const canSeeData = (user && isSearchable(user)) || acceptedFollower
  const { data: reviewsData } = useGetUserReviewsQuery(
    { handle: user?.handle || "", page: 1, perPage: 1 },
    { skip: !user?.handle }
  )
  const reviewsCount = reviewsData?.meta.total_count

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: "50%",
  })

  useEffect(() => {
    if (inView && user) {
      if (tab === "guides" && currInspirationPage < maxInspirationPage) {
        void dispatch(handlePublicUserGuidesNext({ user }))
      } else if (
        tab === "collections" &&
        currCollectionsPage < maxCollectionsPage
      ) {
        void dispatch(handlePublicUserCollectionsNext(user))
      }
    }
  }, [
    inView,
    tab,
    currInspirationPage,
    maxInspirationPage,
    currCollectionsPage,
    maxCollectionsPage,
    dispatch,
    user,
  ])
  const handleTabChange = useCallback(
    (tab: string) => {
      if (!canSeeData) return
      const newTab = tab as PublicProfileTabType
      dispatch(publicProfileSlice.actions.setPublicTab(newTab))
      if (user) {
        void dispatch(
          setPublicTabWithData({
            tab: newTab,
            user,
          })
        )
      }
    },
    [canSeeData, dispatch, user]
  )

  // Remount can run before Redux resets; don't auto-switch on stale counts.
  const isCurrentProfileData = user?.handle === handle

  useProfileTabAutoSwitch({
    tab,
    collectionsCount,
    reviewsCount,
    guidesCount,
    onTabChange: handleTabChange,
    enabled: canSeeData && isCurrentProfileData,
  })

  if (!user || !userProfile) return null

  return (
    <Container className="flex flex-1 flex-col pt-9 sm:pt-7">
      <ProfileHeader
        user={user}
        userProfile={userProfile}
        isCurrentUser={false}
        guidesCount={guidesCount}
        collectionsCount={collectionsCount}
      />
      <div className="mt-9 flex flex-1 flex-col">
        <Tabs
          value={tab}
          className="flex flex-1 flex-col"
          onValueChange={handleTabChange}
        >
          <TabsList className="justify-center max-sm:gap-1 max-sm:text-xs">
            <TabsTrigger value="guides" disabled={!canSeeData}>
              <IconChecklist className="mt-[-.125em] mr-[.25em] inline-block text-[1.5em]" />
              Guides{" "}
              {canSeeData && (
                <span className="ml-[.25em] font-normal">{guidesCount}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="collections" disabled={!canSeeData}>
              <IconHeart className="mt-[-.125em] mr-[.125em] inline-block text-[1.5em]" />
              Collections{" "}
              {canSeeData && (
                <span className="ml-[.25em] font-normal">
                  {collectionsCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="reviews" disabled={!canSeeData}>
              <IconStar className="mt-[-.125em] mr-[.125em] inline-block text-[1.5em]" />
              Reviews{" "}
              {canSeeData && (
                <span className="ml-[.25em] font-normal">
                  {reviewsCount ?? 0}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
          <div className="relative flex flex-1 flex-col">
            {!canSeeData && (
              <div className="flex flex-1 flex-col items-center justify-center gap-5 pb-32">
                <p className="text-lg font-medium">This account is private.</p>
                <IconLock className="text-7xl text-muted" strokeWidth={0.5} />
              </div>
            )}
            {canSeeData && (
              <>
                <TabsContent
                  value="guides"
                  className="flex flex-1 flex-col pt-9 pb-20"
                >
                  {guidesCount === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center text-center">
                      <LottieAnimation
                        name="guide"
                        width={190}
                        height={190}
                        className="mx-auto mb-5"
                      />
                      <h2 className="mb-2 text-md font-semibold sm:text-lg">
                        <span>{userDisplayShortName(user)}</span> hasn’t
                        published any guides yet.
                      </h2>
                      <p className="sm:text-md">Check back in later.</p>
                    </div>
                  ) : (
                    <>
                      <CardGrid>
                        {guides.length === 0
                          ? Array.from({
                              length: Math.min(
                                guidesCount,
                                DefaultInspirationPerPage
                              ),
                            }).map((_, idx) => (
                              <li key={idx}>
                                <InspirationCard
                                  trackingContext="CardGrid"
                                  entitySheetTrackingContext="ProfileGuide"
                                />
                              </li>
                            ))
                          : guides.map((guide) => (
                              <li key={guide.entity_ref}>
                                <InspirationCard
                                  inspiration={guide}
                                  isLinked
                                  enableHandleLink={false}
                                  trackingContext="CardGrid"
                                  entitySheetTrackingContext="ProfileGuide"
                                />
                              </li>
                            ))}
                      </CardGrid>
                      {guides.length > 0 && (
                        <LoadingPageIndicator isLoading={loadingGuides} />
                      )}
                      <div ref={loadMoreRef} />
                    </>
                  )}
                </TabsContent>
                <TabsContent
                  value="collections"
                  className="flex flex-1 flex-col pt-9 pb-20"
                >
                  {collectionsCount === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center text-center">
                      <LottieAnimation
                        name="collect"
                        width={190}
                        height={190}
                        className="mx-auto mb-5"
                      />
                      <h2 className="mb-2 text-md font-semibold sm:text-lg">
                        <span>{userDisplayShortName(user)}</span> hasn’t created
                        any collections yet.
                      </h2>
                      <p className="sm:text-md">Check back in later.</p>
                    </div>
                  ) : (
                    <>
                      <CardGrid>
                        {collections.length === 0
                          ? Array.from({
                              length: Math.min(
                                collectionsCount,
                                DEFAULT_PLACES_PER_PAGE
                              ),
                            }).map((_, idx) => (
                              <li key={idx}>
                                <CollectionCard />
                              </li>
                            ))
                          : collections.map((collection) => (
                              <li key={collection.ref}>
                                <CollectionCard collection={collection} />
                              </li>
                            ))}
                      </CardGrid>
                      {collections.length > 0 && (
                        <LoadingPageIndicator isLoading={loadingCollections} />
                      )}
                      <div ref={loadMoreRef} />
                    </>
                  )}
                </TabsContent>
                <TabsContent
                  value="reviews"
                  className="flex flex-1 flex-col pt-9 pb-20"
                >
                  <ProfileReviewsList mode="public" user={user} />
                </TabsContent>
              </>
            )}
          </div>
        </Tabs>
      </div>
    </Container>
  )
}
